import {
    KeyboardArrowUpOutlined,
    KeyboardArrowDownOutlined,
} from "@mui/icons-material";
import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useTranslation } from "../../../../explorer/hooks/useTranslation";
import { Filters, UpdateFilters } from "./Filters";
import { useState, useEffect } from "react";
import {
    OfferStatusType,
    OrderStatusType,
} from "../../../../explorer/types/Basket/OfferOrderStatusType";
import { OfferOrderSortingType } from "../../../../explorer/types/Basket/OfferOrderSortingType";
import { OrderType } from "../../../../explorer/types/Basket/OrderType";
import {
    AccordionDetailsStyled,
    AccordionStyled,
    FormControlStyled,
} from "./OrderOfferHistoryFilter.style";
import { AppIconsArrow, AppIconsDate } from "../../../components/AppIcons";
import TextBackButton from "../../../components/AppButtons/TextBackButton";
import TextConfirmButton from "../../../components/AppButtons/TextConfirmButton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTime } from "luxon";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import useLanguages from "../../../../explorer/hooks/useLanguages";
import { StyleguideSavioColors, StyleguideSavioFonts } from "../../../configuration/themeSettings";
import useCurrentSite from "../../../hook/useCurrentSite";

interface OrderOfferHistoryFilterProps {
    filters: Filters;
    tabValue: number;
    updateFilters: UpdateFilters;
    isFetching: boolean;
    accordionExpanded: boolean;
    setAccordionExpanded: React.Dispatch<React.SetStateAction<boolean>>;
    isOrder: boolean;
}

export const OrderOfferHistoryFilter = ({
    filters,
    tabValue,
    updateFilters,
    isFetching,
    accordionExpanded,
    setAccordionExpanded,
    isOrder,
}: OrderOfferHistoryFilterProps) => {
    const { t } = useTranslation();
    const [localFilters, setLocalFilters] = useState(filters);

    const lang = useLanguages().currentLanguage;

    const filteredStatuses = isOrder
        ? Object.values(OrderStatusType) // Mostra tutti i campi se isOrder è true
        : Object.values(OfferStatusType); // Esclude SHIPPED se isOrder è false

    const [currentSite, setCurrentSite] = useCurrentSite();
    const salesTypeEnabled = currentSite?.permissions?.canAccessOrderHistory === true;
    const serviceTypeEnabled = currentSite?.permissions?.canAccessServiceOrderHistory === true;

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    const handleAccordionToggle = () => {
        setAccordionExpanded((value) => !value);
    };

    const handleCancelFilters = () => {
        const resetFilters = {
            filterStatus: undefined,
            filterCreateDateStart: undefined,
            filterCreateDateStop: undefined,
            filterPartNumber: undefined,
            sortingType: OfferOrderSortingType.CreateDateDesc,
            offerCode: undefined,
            orderCode: undefined,
            customerOfferCode: undefined,
            customerOrderCode: undefined,
            customerReference: undefined,
            filterOrderType: undefined,
        };
        setLocalFilters(resetFilters);
        updateFilters(resetFilters);
    };

    return (
      <Box sx={{ width: "100%" }}>
        <AccordionStyled expanded={accordionExpanded}>
          <Button
            onClick={handleAccordionToggle}
            variant="text"
            color="primary"
            style={{
              textTransform: "uppercase",
            }}
            startIcon={
              accordionExpanded ? (
                <KeyboardArrowUpOutlined />
              ) : (
                <KeyboardArrowDownOutlined />
              )
            }
          >
            {" "}
            {t("common.spareparts.search.display_filters")}
          </Button>
          <AccordionDetailsStyled>
            <FormControl
              fullWidth
              margin="normal"
              disabled={isFetching}
              variant="standard"
            >
              <InputLabel>{t("common.spareparts.part.status")}</InputLabel>
              <Select
                name="filterStatus"
                IconComponent={() => (
                  <AppIconsArrow
                    sx={{
                      transform: "rotate(90deg)",
                      fontSize: 15,
                      color: StyleguideSavioColors.mainBlue,
                    }}
                  />
                )}
                variant="standard"
                value={localFilters.filterStatus || ""}
                onChange={(event) =>
                  setLocalFilters({
                    ...localFilters,
                    filterStatus: event.target.value as
                      | OfferStatusType
                      | OrderStatusType,
                  })
                }
              >
                <MenuItem key={"nullValue"} value={undefined}>
                  {t(`common.none`)}
                </MenuItem>
                {filteredStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    {t(
                      `offerOrder.statusType.${status
                        .replace(/\s+/g, "")
                        .toLowerCase()}`
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              margin="normal"
              sx={{ marginTop: "-2px  !important" }}
            >
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale={lang}
              >
                <Typography
                  sx={{
                    color: StyleguideSavioColors.mainBlue,
                    fontWeight: StyleguideSavioFonts.bold,
                    fontSize: 14,
                    textTransform: "uppercase",
                    marginTop: "11px",
                  }}
                >
                  {t("offerOrder.dateFrom")}
                </Typography>
                <DatePicker
                  sx={{ mt: "-7px" }}
                  value={
                    localFilters.filterCreateDateStart
                      ? DateTime.fromFormat(
                          localFilters.filterCreateDateStart,
                          "yyyyLLdd"
                        )
                      : null
                  }
                  slots={{
                    openPickerIcon: AppIconsDate,
                  }}
                  slotProps={{
                    textField: { variant: "standard" },
                  }}
                  onChange={(event) =>
                    setLocalFilters({
                      ...localFilters,
                      filterCreateDateStart: event
                        ?.toFormat("yyyyLLdd")
                        .toString(),
                    })
                  }
                  disabled={isFetching}
                  maxDate={DateTime.fromFormat(
                    localFilters.filterCreateDateStop ?? "",
                    "yyyyLLdd"
                  )}
                />
              </LocalizationProvider>
            </FormControl>
            <FormControl
              fullWidth
              margin="normal"
              sx={{ marginTop: "-2px  !important" }}
            >
              <LocalizationProvider
                dateAdapter={AdapterLuxon}
                adapterLocale={lang}
              >
                <Typography
                  sx={{
                    color: StyleguideSavioColors.mainBlue,
                    fontWeight: StyleguideSavioFonts.bold,
                    fontSize: 14,
                    textTransform: "uppercase",
                    marginTop: "11px",
                  }}
                >
                  {t("offerOrder.dateTo")}
                </Typography>
                <DatePicker
                  sx={{ mt: "-7px" }}
                  value={
                    localFilters.filterCreateDateStop
                      ? DateTime.fromFormat(
                          localFilters.filterCreateDateStop,
                          "yyyyLLdd"
                        )
                      : null
                  }
                  slots={{
                    openPickerIcon: AppIconsDate,
                  }}
                  slotProps={{
                    textField: { variant: "standard" },
                  }}
                  onChange={(event) =>
                    setLocalFilters({
                      ...localFilters,
                      filterCreateDateStop: event
                        ?.toFormat("yyyyLLdd")
                        .toString(),
                    })
                  }
                  disabled={isFetching}
                  minDate={DateTime.fromFormat(
                    localFilters.filterCreateDateStart ?? "",
                    "yyyyLLdd"
                  )}
                />
              </LocalizationProvider>
              {/* <TextField
                            label={t("Create Date Stop")}
                            type="date"
                            name="filterCreateDateStop"
                            value={localFilters.filterCreateDateStop || ""}
                            placeholder={t("offerOrder.dateTo")}
                            variant="standard"
                            slots={{}}
                            InputLabelProps={{ shrink: true }}
                            onChange={(event) =>
                                setLocalFilters({
                                    ...localFilters,
                                    filterCreateDateStop: event.target.value,
                                })
                            }
                            disabled={isFetching}
                        /> */}
            </FormControl>
            <FormControl fullWidth margin="normal">
              <TextField
                label={t("common.spareparts.part.partcode")}
                name="filterPartNumber"
                variant="standard"
                value={localFilters.filterPartNumber || ""}
                onChange={(event) =>
                  setLocalFilters({
                    ...localFilters,
                    filterPartNumber: event.target.value,
                  })
                }
                disabled={isFetching}
              />
            </FormControl>
            <FormControl
              fullWidth
              margin="normal"
              disabled={isFetching}
              variant="standard"
            >
              <InputLabel>
                {t("common.spareparts.search.orderedType.selectBy")}
              </InputLabel>
              <Select
                name="sortingType"
                IconComponent={() => (
                  <AppIconsArrow
                    sx={{
                      transform: "rotate(90deg)",
                      fontSize: 15,
                      color: StyleguideSavioColors.mainBlue,
                    }}
                  />
                )}
                variant="standard"
                value={localFilters.sortingType || ""}
                defaultValue={localFilters.sortingType}
                onChange={(event) =>
                  setLocalFilters({
                    ...localFilters,
                    sortingType: event.target.value as string,
                  })
                }
              >
                {Object.values(OfferOrderSortingType).map((sorting) => (
                  <MenuItem key={sorting} value={sorting}>
                    {t(
                      `offerOrder.sortingType.${sorting
                        .replace(/\s+/g, "")
                        .toLowerCase()}`
                    )}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {tabValue === 1 && (
              <FormControl fullWidth margin="normal">
                <TextField
                  label={t("offerOrder.offerCode")}
                  name="offerCode"
                  variant="standard"
                  value={localFilters.offerCode || ""}
                  onChange={(event) =>
                    setLocalFilters({
                      ...localFilters,
                      offerCode: event.target.value,
                    })
                  }
                  disabled={isFetching}
                />
              </FormControl>
            )}
            {tabValue === 0 && (
              <FormControl fullWidth margin="normal">
                <TextField
                  label={t("offerOrder.orderCode")}
                  name="orderCode"
                  variant="standard"
                  value={localFilters.orderCode || ""}
                  onChange={(event) =>
                    setLocalFilters({
                      ...localFilters,
                      orderCode: event.target.value,
                    })
                  }
                  disabled={isFetching}
                />
              </FormControl>
            )}
            {tabValue === 1 && (
              <FormControl fullWidth margin="normal">
                <TextField
                  label={t("offerOrder.customerOfferCode")}
                  name="customerOfferCode"
                  variant="standard"
                  value={localFilters.customerOfferCode || ""}
                  onChange={(event) =>
                    setLocalFilters({
                      ...localFilters,
                      customerOfferCode: event.target.value,
                    })
                  }
                  disabled={isFetching}
                />
              </FormControl>
            )}
            {tabValue === 0 && (
              <FormControl fullWidth margin="normal">
                <TextField
                  label={t("offerOrder.customerOrderCode")}
                  name="customerOrderCode"
                  variant="standard"
                  value={localFilters.customerOrderCode || ""}
                  onChange={(event) =>
                    setLocalFilters({
                      ...localFilters,
                      customerOrderCode: event.target.value,
                    })
                  }
                  disabled={isFetching}
                />
              </FormControl>
            )}
            {tabValue === 0 && (
              <FormControlStyled
                fullWidth
                margin="normal"
                disabled={isFetching}
              >
                <InputLabel>{t("orderOffer.orderType")}</InputLabel>
                <Select
                  name="filterType"
                  variant="standard"
                  value={localFilters.filterOrderType || ""}
                  IconComponent={() => (
                    <AppIconsArrow
                      sx={{
                        transform: "rotate(90deg)",
                        fontSize: 15,
                        color: StyleguideSavioColors.mainBlue,
                      }}
                    />
                  )}
                  onChange={(event) =>
                    setLocalFilters({
                      ...localFilters,
                      filterOrderType: event.target.value as OrderType,
                    })
                  }
                >
                  {salesTypeEnabled && serviceTypeEnabled && (
                    <MenuItem key={"noneType"} value={undefined}>
                      {t(`common.none`)}
                    </MenuItem>
                  )}
                  {Object.values(OrderType)
                  .filter((type) => (salesTypeEnabled && type === OrderType.Sales) || (serviceTypeEnabled && type === OrderType.Service))
                  .map((type) => (
                    <MenuItem key={type} value={type}>
                      {t(`offerOrder.${type}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControlStyled>
            )}
            <FormControl fullWidth margin="normal">
              <TextField
                label={t("offerOrder.customerReference")}
                name="customerReference"
                variant="standard"
                value={localFilters.customerReference || ""}
                onChange={(event) =>
                  setLocalFilters({
                    ...localFilters,
                    customerReference: event.target.value,
                  })
                }
                disabled={isFetching}
              />
            </FormControl>
            <Box display="flex" justifyContent="space-between" mt={2}>
              <TextBackButton
                variant="contained"
                color="primary"
                onClick={handleCancelFilters}
                disabled={isFetching}
              >
                {t("common.spareparts.filter.reset_lbl")}
              </TextBackButton>
              <TextConfirmButton
                onClick={() => updateFilters(localFilters)}
                disabled={isFetching}
                sx={{
                  ml: "10px",
                  minWidth: "160px",
                }}
              >
                {t("common.spareparts.filter.applyfilters")}
              </TextConfirmButton>
            </Box>
          </AccordionDetailsStyled>
        </AccordionStyled>
      </Box>
    );
};
